/**
 * Service for account deletion functionality (GDPR compliance)
 * 
 * Handles API calls for:
 * - Getting deletion delay setting
 * - Requesting account deletion
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

interface DeletionDelayResponse {
  delayDays: number;
}

interface DeletionRequestResponse {
  message: string;
  deletionScheduledAt: string;
}

/**
 * Get the account deletion delay setting (in days).
 * 
 * @param token - Auth token
 * @returns API response with delay in days
 */
export async function getDeletionDelay(
  token: string
): Promise<ApiResponse<DeletionDelayResponse>> {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/user/deletion-delay`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting deletion delay:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Network error',
      },
    };
  }
}

/**
 * Request account deletion. This will:
 * - Mark the account for deletion
 * - Deactivate the account immediately
 * - Send a confirmation email
 * - Log the user out
 * 
 * @param token - Auth token
 * @returns API response with deletion scheduled date
 */
export async function requestDeletion(
  token: string
): Promise<ApiResponse<DeletionRequestResponse>> {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/user/request-deletion`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error requesting deletion:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Network error',
      },
    };
  }
}

export const accountDeletionService = {
  getDeletionDelay,
  requestDeletion,
};

export default accountDeletionService;
